import React, { useRef, useState, useEffect } from "react";
import { useSearchBox, useHits, Index, Highlight } from "react-instantsearch";
import { ModalFiche } from "./ModalFiche";
import { ALGOLIA_INDEX_FICHES } from "../../api/algolia";

const inputStyle = {
    width: "100%",
    padding: "10px",
    border: "1px solid #ddd",
    marginTop: 4,
    borderBottom: "2px solid rgb(58, 58, 58)",
    borderRadius: "4px 4px 0px 0px"
};

/**
 * CustomAutoComplete — composant réutilisable
 *
 * Props :
 *   - indexName         : nom de l'index Algolia  (défaut: "ft_fiches")
 *   - labelField        : champ du hit à afficher  (défaut: "terme_m")
 *   - name              : name de l'input          (défaut: "autocomplete")
 *   - placeholder       : placeholder de l'input   (défaut: "Rechercher...")
 *   - selectedIds       : array des objectID sélectionnés (géré par le parent)
 *   - onSelectionChange : callback appelé à chaque ajout/retrait
 *
 * Usage :
 *   const [selectedIds, setSelectedIds] = useState([]);
 *
 *   <CustomAutoComplete
 *     indexName="ft_fiches"
 *     labelField="terme_m"
 *     name="voirAussi"
 *     placeholder="Rechercher un terme..."
 *     selectedIds={selectedIds}
 *     onSelectionChange={setSelectedIds}
 *   />
 */

export function CustomAutoComplete({
                                       indexName    = ALGOLIA_INDEX_FICHES,
                                       labelField   = "terme_m",
                                       name         = "autocomplete",
                                       placeholder  = "Rechercher...",
                                       selectedIds  = [],
                                       onSelectionChange,
                                       singleSelect = false,
                                       initialLabels = {},
                                   }) {
    return (
        <Index indexName={indexName}>
            <AutoCompleteInner
                labelField={labelField}
                name={name}
                placeholder={placeholder}
                selectedIds={selectedIds}
                onSelectionChange={onSelectionChange}
                singleSelect={singleSelect}
                initialLabels={initialLabels}
            />
        </Index>
    );
}

// Ajoute singleSelect ici
function AutoCompleteInner({ labelField, name, placeholder, selectedIds, onSelectionChange, singleSelect, initialLabels }) {
    const wrapperRef = useRef(null);
    const inputRef   = useRef(null);

    const { query, refine } = useSearchBox();
    const { hits = [] }     = useHits();

    const [value,           setValue          ] = useState(query || "");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex,     setActiveIndex    ] = useState(-1);
    const [selectedHit,     setSelectedHit    ] = useState(null);
    const [selectedLabels,  setSelectedLabels ] = useState({});

    // ─── Sélection / retrait ──────────────────────────────────────────────────

    const toggleSelection = (hit) => {
        const isAlreadySelected = selectedIds.includes(hit.objectID);

        if (isAlreadySelected) {
            // Retrait
            onSelectionChange([], {});
            setSelectedLabels({});
        } else {
            if (singleSelect) {
                // ✅ Mode single : remplace l'ancien
                const nextLabels = { [hit.objectID]: hit[labelField] };
                onSelectionChange([hit.objectID], nextLabels);
                setSelectedLabels(nextLabels);
            } else {
                // Mode multi : ajout
                const nextIds = [...selectedIds, hit.objectID];
                const nextLabels = { ...selectedLabels, [hit.objectID]: hit[labelField] };
                onSelectionChange(nextIds, nextLabels);
                setSelectedLabels(nextLabels);
            }
        }
    };

    const removeTag = (objectID) => {
        const nextIds = selectedIds.filter(id => id !== objectID);
        const nextLabels = { ...selectedLabels };
        delete nextLabels[objectID];
        onSelectionChange(nextIds, nextLabels);
        setSelectedLabels(nextLabels);
    };

    // ─── Handlers input ───────────────────────────────────────────────────────

    const handleChange = (e) => {
        const newValue = e.currentTarget.value;
        setValue(newValue);
        setActiveIndex(-1);
        if (newValue.length === 0) {
            refine("");
            setShowSuggestions(false);
        } else if (newValue.length >= 3) {
            refine(newValue);
            setShowSuggestions(true);
        }
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(prev => Math.max(prev - 1, -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && suggestions[activeIndex]) {
                selectSuggestion(suggestions[activeIndex][labelField]);
            } else {
                setShowSuggestions(false);
            }
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
            setActiveIndex(-1);
        }
    };

    const selectSuggestion = (label) => {
        setValue(label);
        refine(label);
        setShowSuggestions(false);
        setActiveIndex(-1);
        inputRef.current?.focus();
    };

    // ─── Clic extérieur ───────────────────────────────────────────────────────

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
                setActiveIndex(-1);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setValue(query || "");
    }, [query]);

    // ─── Pré-remplissage des libellés (mode édition) ──────────────────────────
    useEffect(() => {
        if (initialLabels && Object.keys(initialLabels).length) {
            setSelectedLabels(prev => ({ ...initialLabels, ...prev }));
        }
    }, [initialLabels]);

    // ─── Suggestions filtrées + dédoublonnées ─────────────────────────────────

    const suggestions = hits
        .filter(hit => hit[labelField] && hit[labelField].trim() !== "")
        .filter((hit, index, self) =>
            index === self.findIndex(h => h[labelField] === hit[labelField])
        )
        .slice(0, 8);

    // ─── Rendu ────────────────────────────────────────────────────────────────

    return (
        <>
            <div ref={wrapperRef} style={{ position: "relative" }}>

                {/* Input de recherche */}
                <input
                    ref={inputRef}
                    type="search"
                    name={name}           // ✅ prop name passée en paramètre
                    value={value}
                    style={inputStyle}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => value.length >= 3 && suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder={placeholder}
                    className="autocomplete-input fr-input"
                    autoComplete="off"
                    aria-autocomplete="list"
                    aria-expanded={showSuggestions}
                    aria-activedescendant={activeIndex >= 0 ? `suggestion-${name}-${activeIndex}` : undefined}
                />

                {/* Tags des mots sélectionnés */}
                {selectedIds.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {selectedIds.map(id => (
                            <span
                                key={id}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    background: "#dbeafe",
                                    color: "#1e40af",
                                    border: "1px solid #93c5fd",
                                    borderRadius: 20,
                                    padding: "3px 10px",
                                    fontSize: 13,
                                    fontWeight: 500,
                                }}
                            >
                                {selectedLabels[id] || id}
                                <button
                                    type="button"
                                    onClick={() => removeTag(id)}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#1e40af",
                                        fontWeight: "bold",
                                        fontSize: 14,
                                        lineHeight: 1,
                                        padding: 0,
                                    }}
                                    title={`Retirer ${selectedLabels[id] || id}`}
                                >
                                    ✕
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* Dropdown suggestions */}
                {showSuggestions && suggestions.length > 0 && (
                    <ul
                        role="listbox"
                        style={{
                            position: "absolute",
                            top: "42px",
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            margin: 0,
                            padding: 0,
                            listStyle: "none",
                            background: "#fff",
                            border: "1px solid #ccc",
                            borderTop: "none",
                            borderRadius: "0 0 6px 6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            maxHeight: 400,
                            overflowY: "auto",
                        }}
                    >
                        {suggestions.map((hit, index) => {
                            const isSelected = selectedIds.includes(hit.objectID);
                            return (
                                <li
                                    key={hit.objectID}
                                    id={`suggestion-${name}-${index}`}
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "default",
                                        background: isSelected
                                            ? "#eef6ff"
                                            : index === activeIndex ? "#f0f0f0" : "#fff",
                                        borderBottom: "1px solid #f0f0f0",
                                        fontSize: 14,
                                        color: "#1a1a1a",
                                        display: "grid",
                                        gridTemplateColumns: "1fr auto",
                                        alignItems: "center",
                                        gap: 8,
                                    }}
                                >
                                    {/* Partie texte — ✅ Highlight Algolia */}
                                    <span
                                        style={{ cursor: "pointer", overflow: "hidden" }}
                                        onMouseDown={() => selectSuggestion(hit[labelField])}
                                    >
                                        {/* ✅ Highlight natif Algolia — remplace notre Highlight custom */}
                                        <Highlight hit={hit} attribute={labelField} />
                                        <span style={{ color: "#888", fontSize: 11, marginLeft: 6 }}>
                                            {hit.num}{hit?.commission ? ` — ${hit.commission}` : ""}
                                        </span>
                                    </span>

                                    {/* Boutons empilés */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setSelectedHit(hit);
                                            }}
                                            style={{ fontSize: 11, padding: "2px 8px", whiteSpace: "nowrap" }}
                                        >
                                            Voir
                                        </button>

                                        <button
                                            type="button"
                                            className={`btn btn-sm ${isSelected ? "btn-success" : "btn-outline-secondary"}`}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleSelection(hit);
                                            }}
                                            style={{ fontSize: 11, padding: "2px 8px", whiteSpace: "nowrap" }}
                                        >
                                            {isSelected ? "✓ Sélectionné" : "Sélectionner"}
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <ModalFiche
                hit={selectedHit}
                onClose={() => setSelectedHit(null)}
            />
        </>
    );
}